import React from "react"
import { cn } from "@/lib/utils"
import { CheckIcon, LockIcon, PlayIcon, FileQuestionIcon, InfinityIcon } from "lucide-react"

export interface ChapterItem {
  id: string
  type: "video" | "quiz" | "brainstorming" | "meetup" | "activity"
  title: string
  href?: string | null
  isLocked?: boolean
  isCompleted?: boolean
  isCurrent?: boolean
}

export interface Chapter {
  id: string
  title: string
  items?: ChapterItem[]
  // Legacy flat-chapter fields (kept for backwards compatibility)
  number?: number
  duration?: string
  isCompleted?: boolean
  isLocked?: boolean
  isCurrent?: boolean
}

export interface ChapterListProps {
  /**
   * Module title
   */
  moduleTitle: string

  /**
   * List of chapters
   */
  chapters: Chapter[]

  /**
   * Callback when a chapter is clicked
   */
  onChapterClick?: (chapterId: string) => void

  /**
   * Additional CSS classes
   */
  className?: string
}

export const ChapterList: React.FC<ChapterListProps> = ({
  moduleTitle,
  chapters,
  onChapterClick,
  className
}) => {
  const hasItems = chapters.some(ch => ch.items && ch.items.length > 0)

  return (
    <div className={cn("space-y-4", className)}>
      {/* Module Title */}
      <div className="px-4 py-3 border-b border-border">
        <h3 className="font-semibold text-foreground">{moduleTitle}</h3>
      </div>

      {hasItems ? (
        /* Nested chapter/item view */
        <div className="space-y-2">
          {chapters.map((chapter, chapterIdx) => (
            <div key={chapter.id}>
              <div className="px-4 py-2">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Chapter {chapterIdx + 1}: {chapter.title}
                </div>
              </div>
              <div className="space-y-0.5">
                {(chapter.items || []).map((item) => {
                  const handleClick = () => {
                    if (!item.isLocked && item.href) {
                      window.location.href = item.href
                    }
                  }

                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      onClick={handleClick}
                      disabled={item.isLocked}
                      className={cn(
                        "w-full text-left px-4 py-2.5 transition-colors relative",
                        "hover:bg-muted/50",
                        item.isCurrent && "bg-muted border-l-4 border-l-primary",
                        item.isLocked && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {/* Status Icon */}
                        <div className="shrink-0">
                          {item.type === "activity" ? (
                            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                              <InfinityIcon className="w-3 h-3 text-white" />
                            </div>
                          ) : item.isCompleted ? (
                            <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
                              <CheckIcon className="w-3 h-3 text-white" />
                            </div>
                          ) : item.isLocked ? (
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                              <LockIcon className="w-3 h-3 text-muted-foreground" />
                            </div>
                          ) : item.isCurrent ? (
                            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                              {item.type === "quiz" ? (
                                <FileQuestionIcon className="w-3 h-3 text-primary-foreground" />
                              ) : (
                                <PlayIcon className="w-3 h-3 text-primary-foreground" />
                              )}
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-border flex items-center justify-center">
                              {item.type === "quiz" ? (
                                <FileQuestionIcon className="w-3 h-3 text-muted-foreground" />
                              ) : (
                                <PlayIcon className="w-3 h-3 text-muted-foreground" />
                              )}
                            </div>
                          )}
                        </div>

                        {/* Item Info */}
                        <div className="flex-1 min-w-0">
                          <div
                            className={cn(
                              "text-sm font-medium line-clamp-2",
                              item.isCurrent
                                ? "text-foreground"
                                : "text-muted-foreground"
                            )}
                          >
                            {item.title}
                          </div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {item.type === "activity" ? "Validation" : item.type}
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Legacy flat chapter list */
        <div className="space-y-1">
          {chapters.map((chapter) => (
            <button
              key={chapter.id}
              onClick={() => !chapter.isLocked && onChapterClick?.(chapter.id)}
              disabled={chapter.isLocked}
              className={cn(
                "w-full text-left px-4 py-3 transition-colors relative",
                "hover:bg-muted/50",
                chapter.isCurrent && "bg-muted border-l-4 border-l-primary",
                chapter.isLocked && "opacity-60 cursor-not-allowed"
              )}
            >
              {chapter.isCurrent && !chapter.isLocked && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
              )}

              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  {chapter.isCompleted ? (
                    <div className="w-5 h-5 rounded-full bg-green-600 dark:bg-green-600 flex items-center justify-center">
                      <CheckIcon className="w-3 h-3 text-white" />
                    </div>
                  ) : chapter.isLocked ? (
                    <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                      <LockIcon className="w-3 h-3 text-muted-foreground" />
                    </div>
                  ) : chapter.isCurrent ? (
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                      <PlayIcon className="w-3 h-3 text-primary-foreground" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-border" />
                  )}
                </div>

                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-muted-foreground mb-1">
                        Chapter {chapter.number}
                      </div>
                      <div
                        className={cn(
                          "text-sm font-medium line-clamp-2",
                          chapter.isCurrent
                            ? "text-foreground"
                            : "text-muted-foreground"
                        )}
                      >
                        {chapter.title}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {chapter.duration}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
